import React, { useContext, useState, useMemo } from "react"; // Added useMemo for optimization
import { Card, Spinner, Button, Modal, TextInput, Label } from "flowbite-react";
import { Store } from "../context/store";
import { useNavigate } from "react-router-dom";
import apiRequest from "../utils/apiRequest";
import { HiOutlineExclamationCircle, HiLockClosed, HiCheckCircle, HiEye, HiEyeOff } from "react-icons/hi";
import { confirmDialog } from "../components/ConfirmDialog";
import DashboardLayout from "../components/layout/DashboardLayout";

function validatePassword(password) {
  const re = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-_]).{8,}$/;
  return re.test(password);
}

// Refactored PasswordInputWithToggle to fix the focus loss issue
const PasswordInputWithToggle = React.memo(({ id, label, value, setter, showState, setShowState, disabled, color, hint }) => {
  // Use useMemo to create a stable icon element
  const ToggleIcon = useMemo(() => {
    return (
      <div 
          // Use an absolute position wrapper if possible, or just the cursor styles
          className="cursor-pointer p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full" 
          onClick={() => setShowState(prev => !prev)}
      >
          {showState ? <HiEyeOff className="h-5 w-5 text-gray-500" /> : <HiEye className="h-5 w-5 text-gray-500" />}
      </div>
    );
  }, [showState, setShowState]); // Recalculate only when visibility state or setter changes

  return (
    <div>
      <div className="mb-2 block">
        <Label htmlFor={id} value={label} />
      </div>
      <TextInput
        id={id}
        placeholder="••••••••"
        required
        name={id}
        value={value}
        type={showState ? "text" : "password"}
        onChange={(e) => setter(e.target.value)}
        disabled={disabled}
        color={color || "default"}
        // Pass the stable element to rightIcon
        rightIcon={() => ToggleIcon} 
      />
      {hint && <p className={`mt-1 text-xs ${color === "failure" ? 'text-red-500' : 'text-green-500'}`}>{hint}</p>}
    </div>
  );
});

PasswordInputWithToggle.displayName = 'PasswordInputWithToggle';

function Settings() {
  let store = useContext(Store);
  let [baseUrl] = store.url;
  let [msg, setMsg] = useState("");
  let [error, setError] = useState("");
  let [loading1, setLoading1] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // State for password visibility toggles
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [openModalOut, setOpenModalOut] = useState(false);
  const navigate = useNavigate();

  // Helper to check individual password criteria for visual feedback
  const passwordCriteria = {
    minLength: newPassword.length >= 8,
    hasUpper: /[A-Z]/.test(newPassword),
    hasLower: /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
    hasSpecial: /[#?!@$%^&*-_]/.test(newPassword),
    isMatch: newPassword === confirmPassword && confirmPassword.length > 0
  };

  const PasswordRequirementItem = ({ condition, text }) => (
    <li className={`text-sm ${condition ? 'text-green-500' : 'text-gray-500 dark:text-gray-400'}`}>
      <span className="flex items-center">
        {condition ? <HiCheckCircle className="mr-2 h-4 w-4 text-green-500" /> : <HiLockClosed className="mr-2 h-4 w-4 text-gray-500" />}
        {text}
      </span>
    </li>
  );

  const changePassword = async () => {
    // Clear previous error
    setError("");
    setMsg(""); 

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError("Please fill all missing fields.");
      return;
    }

    if (oldPassword === newPassword) {
      setError("New password cannnot be the same as current password.");
      return;
    }

    if (confirmPassword !== newPassword) {
      setError("New Password and Confirm Password do not match.");
      return;
    }

    if (!validatePassword(newPassword)) {
      setError("Password does not meet all security requirements.");
      return;
    }

    await confirmDialog(
      "Are you sure you want to change your password?",
      {
        title: "Change Password",
        confirmText: "Yes, change password",
        color: "warning",
        loadingText: "Changing...",
        onConfirm: async () => {
          setLoading1(true);
          try {
            let token = localStorage.getItem("royal-token");
            const url = `${baseUrl}/password/staff`;
            await apiRequest(
              url,
              "PUT",
              { oldPassword, newPassword, confirmPassword },
              token
            );
            setOldPassword("");
            setNewPassword("");
            setConfirmPassword("");
            setMsg("Password changed successfully.");
            setError(""); // Clear error on success
          } catch (error) {
            const unauthorizedErrors = [
              "Access Denied: Invalid or expired token. Please login again",
              "Access Denied: Please log in.",
              "Access Denied: Invalid Token.",
              "Access Denied: Invalid or expired token.",
              "Access Denied: Invalid or Expired Token.",
              "Access Denied: No token provided.",
            ];
            if (unauthorizedErrors.includes(error.message)) {
              setMsg("Session Expired!");
              setOpenModalOut(true);
              localStorage.removeItem("royal-token");
            } else {
              setError(error.message);
              setMsg(""); // Clear message on API error
              console.error(error);
            }
          } finally {
            setLoading1(false);
          }
        },
      },
    );
  };

  let logOut = () => {
    navigate("/");
  };

  return (
    <>
      <title>Profile Settings</title>
      <meta
        name="description"
        content="Site Settings Page to the Royalmines Property Office"
      />
      <DashboardLayout active="settings" contentClassName="lg:p-8">
            <h5 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-6">
              ⚙️ Account Settings
            </h5>
            
            {/* --- Main Content Section --- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* --- Change Password Card (Col 1-2) --- */}
              <Card className="lg:col-span-2 shadow-lg dark:bg-gray-800">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 border-b pb-2">
                  Change Password
                </h3>
                
                <div className="space-y-6">
                  {/* Status Message */}
                  {(error || msg) && (
                    <p className={`text-sm text-center font-medium ${error ? 'text-red-500 dark:text-red-400' : 'text-green-500 dark:text-green-400'}`}>
                      {error || msg}
                    </p>
                  )}

                  <div className="flex flex-col gap-4">
                    {/* Old Password */}
                    <PasswordInputWithToggle
                        id="oldPassword"
                        label="Current Password"
                        value={oldPassword}
                        setter={setOldPassword}
                        showState={showOldPassword}
                        setShowState={setShowOldPassword}
                        disabled={loading1}
                    />
                    
                    {/* New Password */}
                    <PasswordInputWithToggle
                        id="newPassword"
                        label="New Password"
                        value={newPassword}
                        setter={setNewPassword}
                        showState={showNewPassword}
                        setShowState={setShowNewPassword}
                        disabled={loading1}
                    />
                    
                    {/* Confirm New Password */}
                    <PasswordInputWithToggle
                        id="confirmPassword"
                        label="Confirm New Password"
                        value={confirmPassword}
                        setter={setConfirmPassword}
                        showState={showConfirmPassword}
                        setShowState={setShowConfirmPassword}
                        disabled={loading1}
                        color={newPassword.length > 0 && newPassword !== confirmPassword ? "failure" : "default"}
                        hint={newPassword.length > 0 && (newPassword === confirmPassword ? "Passwords match." : "Passwords do not match.")}
                    />

                    {/* Submit Button */}
                    <Button
                      disabled={loading1 || !validatePassword(newPassword) || newPassword !== confirmPassword || oldPassword === newPassword}
                      onClick={changePassword}
                      color="blue"
                      className="w-full mt-4"
                    >
                      {loading1 ? <Spinner size="sm" className="mr-3" /> : null}
                      {loading1 ? "Processing..." : "Set New Password"}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* --- Password Requirements Card (Col 3) --- */}
              <Card className="shadow-lg h-fit dark:bg-gray-800">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                  Password Requirements
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Your new password must meet the following criteria:
                </p>
                <ul unstyled="true" className="space-y-2">
                  <PasswordRequirementItem condition={passwordCriteria.minLength} text="Minimum 8 characters" />
                  <PasswordRequirementItem condition={passwordCriteria.hasUpper} text="At least one uppercase letter (A-Z)" />
                  <PasswordRequirementItem condition={passwordCriteria.hasLower} text="At least one lowercase letter (a-z)" />
                  <PasswordRequirementItem condition={passwordCriteria.hasNumber} text="At least one number (0-9)" />
                  <PasswordRequirementItem condition={passwordCriteria.hasSpecial} text="At least one special character (#?!@$%^&*-_)" />
                </ul>
              </Card>
              
            </div>
      </DashboardLayout>

      {/* modal for logout */}
      <Modal show={openModalOut} size="md" popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              {msg}
            </h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={() => logOut()}>
                {"OK"}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default Settings;
